import type React from 'react';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
	useRef,
	useContext,
} from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { ContextualFormattingEnabledOptions } from '@atlaskit/editor-common/toolbar/types';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode/editorViewmodePluginType';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset/builder';
import type { EditorProps } from '@atlaskit/editor-core/editor';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension/extensionPluginType';
import usePreviousState from '@atlaskit/editor-common/usePreviousState';
import type { ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel/contextPanelPluginType';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility/types';
import type { DiffParams } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';
import type { SyncedBlockFeedbackContext } from '@atlaskit/editor-plugin-synced-block/synced-block-plugin-type';
import type { ExtensionConfiguration } from '@atlaskit/editor-plugin-selection-extension/types';
import type { LimitedModePluginOptions } from '@atlaskit/editor-plugin-limited-mode/limited-mode-plugin-type';
import type { SyncedBlockProvider } from '@atlaskit/editor-synced-block-provider/syncBlockProvider';
import { insm } from '@atlaskit/insm';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { CardContext } from '@atlaskit/link-provider/types';
import { SmartCardContext } from '@atlaskit/link-provider/context';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import type { AiSuggestionsPluginOptions } from '@atlassian/editor-plugin-ai-suggestions';
import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { MarkdownModePluginOptions } from '@atlassian/editor-plugin-markdown-mode/types';
import type { SnippetsPluginOptions } from '@atlassian/editor-plugin-snippets';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { useFileMediaUrlSerializer } from '@confluence/content-markdown-mode/entry-points/useFileMediaUrlSerializer.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import type { LoomProviderConfig } from '@confluence/fabric-providers/entry-points/getLoomProvider.ts';
import { createMentionNodeDataProvider } from '@confluence/fabric-providers/entry-points/MentionNodeDataProvider.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import {
	type MentionType,
	useMentionsNotifications,
} from '@confluence/mentions-notifications/entry-points/useMentionsNotifications.ts';
import { handleMentionsChanged } from '@confluence/mentions-notifications/entry-points/handleMentionsChanged.ts';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import type { EditorAPI } from '@confluence/loom-editor-utils/entry-points/loomEditorUtils-types.ts';
import { useLoomPluginOptions } from '@confluence/loom-editor-utils/entry-points/useLoomPluginOptions.ts';
import { useInitializeLoomPlugin } from '@confluence/loom-editor-utils/entry-points/useInitializeLoomPlugin.ts';
import { useEditorEngagementPlatform } from '@confluence/editor-features/entry-points/useEditorEngagementPlatform.ts';
import { useEditorPluginAIDefinitionsConfig } from '@confluence/editor-features/entry-points/useEditorPluginAIDefinitionsConfig.ts';
import { useEmojiNodeDataProvider } from '@confluence/editor-features/entry-points/useEmojiNodeDataProvider.ts';
import { useEditorPluginAIDefinitionsEnabled } from '@confluence/editor-features/entry-points/useEditorPluginAIDefinitionsEnabled.ts';
import { useSelectionExtensionPluginOptions } from '@confluence/editor-features/entry-points/useSelectionExtensionPluginOptions.ts';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import type { AIGlobalOptIn } from '@confluence/editor-features/entry-points/AIGlobalOptIn.ts';
import type { EditorPluginAIFeedbackHandlersRef } from '@confluence/editor-features/entry-points/EditorPluginAIFeedbackHandlersRef.ts';
import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { type PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { getPageMode } from '@confluence/issue-create-side-panel/entry-points/pageModeUtils.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useTrackChangesPlugin } from '@confluence/onboarding-ai-first-create/entry-points/useTrackChangesPluginOptions.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import { useEditorPreferencesProvider } from '../utils/EditorPreferences.ts';

import { isMainEditorContent } from './isMainEditorContent.ts';
import { EditorLoomButton } from './EditorLoomButton.tsx';
import {
	type CreateFullPageEditorPresetProps,
	createFullPageEditorPreset,
} from './createFullPageEditorPresetNext.ts';
import { createMarkdownEditorPreset } from './createMarkdownEditorPreset.ts';
import { i18n } from './i18n.ts';
import { createPasteOptionsToolbarPluginOptions } from './plugins/pasteOptionsToolbarPluginOptions.tsx';
import { useMauiMove } from './useMauiMove.ts';

const MARKDOWN_SOURCE_WRITEBACK_ERROR_FLAG_ID = 'confluence-markdown-source-writeback-error';

type MarkdownSourceWritebackErrorInfo = Parameters<
	NonNullable<MarkdownModePluginOptions['onSourceWritebackError']>
>[0];

export interface FullPageEditorPresetProps extends WithFlagsProps {
	appearance: FullPageAppearances;
	featureFlags: EditorProps['featureFlags'];
	onSave: EditorProps['onSave'];
	smartCardContext?: CardContext;
	linking: EditorProps['linking'];
	disabled?: EditorProps['disabled'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	media: EditorProps['media'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	annotationProviders: EditorProps['annotationProviders'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	collabEditProvider: EditorProps['collabEditProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	collabEdit: EditorProps['collabEdit'];
	primaryToolbarComponents: EditorProps['primaryToolbarComponents'];
	waitForMediaUpload: EditorProps['waitForMediaUpload'];
	mention: EditorProps['mention'];
	allowFindReplace: EditorProps['allowFindReplace'];
	allowExtension: EditorProps['allowExtension'];
	extensionHandlers: EditorProps['extensionHandlers'];
	referentialityContext: ReferentialityContext;
	editorPluginAIOptions?: ReturnType<typeof createPageEditorPluginAIOptions>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
	elementBrowser: EditorProps['elementBrowser'];
	quickInsert: EditorProps['quickInsert'];
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPINext) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	getHeadingLinkPath: () => string | null;
	shouldHideHeadingAnchorLinks?: boolean;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
	__isRemixHidden?: boolean;
	__isRovoLLMEnabled?: boolean;
	__remixDisabledReason?: 'archived' | 'version-history';
	__livePage?: boolean;
	contextPanelOptions?: ContextPanelPluginOptions;
	contentId: string | undefined;
	spaceId: string | undefined;
	spaceKey: string | undefined;
	isAIEnabledForContent: boolean;
	isUnpublishedDraft: boolean;
	getExtensionHeight?: GetPMNodeHeight;
	contextualFormattingEnabled: boolean;
	contextualFormattingMode?: ContextualFormattingEnabledOptions;
	enableAIPlannerRemoteTextHighlight?: boolean;
	isCompanyHub?: boolean;
	blockLinkHashPrefix?: string;
	showDiffPluginParams?: DiffParams;
	syncedBlockProvider?: SyncedBlockProvider;
	syncedBlockEventHandlers?: EventHandlers;
	onFeedbackPromptShown?: () => Promise<void> | void;
	onGiveFeedback?: (context: SyncedBlockFeedbackContext) => void;
	shouldShowFeedbackPrompt?: () => Promise<boolean> | boolean;
	disableFloatingToolbar?: boolean;
	disableToolbar?: boolean;
	contentMode?: EditorContentMode;
	snippetExtensionList?: ExtensionConfiguration[];
	snippetsPluginOptions?: SnippetsPluginOptions;
	isEmbeddedConfluence?: boolean;
	contentType?: string;
	onShowSuggestionsSidebar?: () => void;
	/**
	 * Options for the AI suggested-edits plugin. Built by the consuming component
	 * via `useSuggestedEditsPluginOptions` so the feedback-collector dialog can be
	 * rendered alongside the editor.
	 */
	aiSuggestionsPluginOptions: AiSuggestionsPluginOptions;
}

export type FullPageEditorPresetAPINext = ExtractPresetAPI<
	ReturnType<typeof useFullPageEditorPreset>
>;

/**
 * Bridges the Rovo store's React state into a stable ref-backed getter that
 * the mentions plugin can call from non-React contexts (setTimeout callbacks,
 * ProseMirror apply()). The subscription is only active when the
 * `platform_editor_agent_mentions` experiment is enabled; when it is
 * disabled this hook returns undefined and does NOT subscribe to the Rovo store.
 */
export function useGetIsRovoPanelOpen(): (() => boolean) | undefined {
	if (!expValEquals('platform_editor_agent_mentions', 'isEnabled', true)) {
		return undefined;
	}
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { mode: rovoMode } = useRovoExperienceAPIStore();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isRovoPanelOpenRef = useRef<boolean>(rovoMode !== undefined);
	isRovoPanelOpenRef.current = rovoMode !== undefined;
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return useCallback(() => isRovoPanelOpenRef.current, []);
}

/**
 * Returns a stable callback that opens Rovo chat with the current editor context
 * when the "Chat with Agent" button on the agent profile card is clicked.
 *
 * Mirrors the nudge's onClick handler in useAgentMentionNudgeAction but publishes
 * directly without writing to the store.
 *
 * Gated behind `platform_editor_agent_mentions`. Returns `undefined` when the gate
 * is off so callers can omit the option entirely.
 */
export function useAgentMentionChatClick():
	| ((agentId: string, agentMentionContext?: DocNode) => void)
	| undefined {
	const publish = usePublish('ai-mate');

	const onClick = useCallback(
		(agentId: string, agentMentionContext?: DocNode) => {
			// agentId is the Agent Studio UUID (resolved from AAID).
			// agentMentionContext is built on chat action click by traversing the live ProseMirror doc.
			publish({
				type: 'chat-new',
				source: 'agentMentionProfileCard',
				openChatMode: 'mini-modal',
				data: {
					...(agentMentionContext ? { prompt: agentMentionContext } : {}),
					overrideAutoSend: true,
					// Draft-only (not auto-sent): skip creating a seeded conversation so the
					// manual send creates and names the conversation. Seeding here is redundant;
					// it only pre-fills the input, and would create a date-named conversation up
					// front, blocking the prompt-based history title.
					skipCreatingSeededConversation: true,
					dialogues: [],
					mode: { useCurrentPageContext: true },
					...(agentId ? { agentId } : {}),
				},
			});
		},
		[publish],
	);
	if (
		!expValEquals('platform_editor_agent_mentions', 'isEnabled', true) ||
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		!fg('platform_editor_agent_mentions_drop_one_fixes')
	) {
		return undefined;
	}
	return onClick;
}

/**
 * Snapshots whether the main editor body had DOM focus when Rovo opened, then
 * restores focus when Rovo closes. Call this once from FullPageEditorComponent,
 * passing the editor API so focus can be restored.
 */
export function useRestoreEditorFocusOnRovoClose(
	editorAPI:
		| { core?: { actions: { focus: (opts: { scrollIntoView: boolean }) => void } } }
		| null
		| undefined,
): void {
	const {
		mode: rovoMode,
		editorHadFocusBeforeChatOpen,
		setEditorHadFocusBeforeChatOpen,
	} = useRovoExperienceAPIStore();
	const editorHadFocusRef = useRef<boolean>(false);
	const prevRovoMode = usePreviousState(rovoMode);

	// Snapshot editor focus when Rovo opens.
	useLayoutEffect(() => {
		if (
			!expValEquals('platform_editor_agent_mentions', 'isEnabled', true) ||
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			!fg('platform_editor_agent_mentions_drop_one_fixes') ||
			!(prevRovoMode === undefined && rovoMode !== undefined)
		) {
			return;
		}
		// Nudge-click path: focus has already moved to the button by the time this
		// runs, so we use the pre-click snapshot from the store.
		// Task-item auto-fire path: no signal is written, so we sample
		// document.activeElement directly.
		if (editorHadFocusBeforeChatOpen === true) {
			editorHadFocusRef.current = true;
			setEditorHadFocusBeforeChatOpen?.(false);
			return;
		}
		editorHadFocusRef.current = isMainEditorContent(document.activeElement);
	}, [rovoMode, prevRovoMode, editorHadFocusBeforeChatOpen, setEditorHadFocusBeforeChatOpen]);

	// Restore focus when Rovo closes.
	useEffect(() => {
		if (
			!expValEquals('platform_editor_agent_mentions', 'isEnabled', true) ||
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			!fg('platform_editor_agent_mentions_drop_one_fixes')
		) {
			return;
		}
		const rovoClosed = prevRovoMode !== undefined && rovoMode === undefined;
		if (!rovoClosed) {
			return;
		}
		const editorHadFocus = editorHadFocusRef.current;
		editorHadFocusRef.current = false;
		if (editorHadFocus) {
			editorAPI?.core?.actions.focus({ scrollIntoView: false });
		}
	}, [rovoMode, prevRovoMode, editorAPI]);
}

// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function useFullPageEditorPreset(props: FullPageEditorPresetProps): any {
	const { showObjectSidebar, hideObjectSidebar, hideObjectSidebarByPanelId } =
		useObjectSidebarActions();

	const isLivePage = useIsCurrentPageLive();
	const { isDesktop } = useIsDesktop();

	const { userId, cloudId, isRovoLLMEnabled } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);
	const previousEditorProps = usePreviousState(props);
	const isClassicMentionsNotificationsEnrolled = useIsMentionOnRestrictedPageEligible();

	const isLivePageRef = useRef<boolean>(isLivePage);
	isLivePageRef.current = isLivePage;

	const getCurrentIsLivePage = useCallback(() => {
		return isLivePageRef.current;
	}, []);

	const { editorPreferencesProvider } = useEditorPreferencesProvider({
		getCurrentIsLivePage,
	});

	const getIsRovoPanelOpen = useGetIsRovoPanelOpen();
	const onAgentMentionChatClickRaw = useAgentMentionChatClick();
	const onAgentMentionChatClickRef = useRef(onAgentMentionChatClickRaw);
	onAgentMentionChatClickRef.current = onAgentMentionChatClickRaw;
	// Stable wrapper created once at mount. Delegates to the ref so the plugin
	// options always receive a concrete function, never undefined.
	const onAgentMentionChatClick = useRef((agentId: string, agentMentionContext?: DocNode) =>
		onAgentMentionChatClickRef.current?.(agentId, agentMentionContext),
	).current;

	const rightSidebarContext = useRightSidebarContext();
	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	const hasEditPermission = props.hasEditPermission || false;
	const hasEditPermissionRef = useRef<boolean>(hasEditPermission);
	hasEditPermissionRef.current = hasEditPermission;

	const pageMode = getPageMode(props.viewMode, props.__livePage, props.hasEditPermission);
	const pageModeRef = useRef<PageMode>(pageMode);
	pageModeRef.current = pageMode;

	useEffect(() => {
		const isMentionNotificationsEnabled =
			isLivePage || (!isLivePage && isClassicMentionsNotificationsEnrolled);

		if (mentionChanges.length && isMentionNotificationsEnabled) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const editorAPIRef = useRef<FullPageEditorPresetAPINext | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: EditorLoomButton,
			editorAPIRef,
			// [ED-27497] IMPORTANT: If you change the code below, please also update the
			// platform/packages/editor/editor-presets-confluence/src/presets/full-page/loomPluginOptions.ts
			editorShouldShowToolbarLoomButton: false,
		});

	const { pluginOptions: trackChangesPluginOptions } = useTrackChangesPlugin({ editorAPIRef });

	// `moveWhiteboardToPage` from `useMauiMove` is intentionally NOT consumed:
	// the backend (monolith `/smart-create/finalize` or convo-ai `/conversation/{id}/action`)
	// handles the cross-space reparent itself, so a frontend GraphQL move is redundant.
	const { duplicateMauiApp } = useMauiMove(props.spaceKey, cloudId, props.contentType);

	const { config: editorEngagementPlatformPluginConfig } = useEditorEngagementPlatform();

	const editorPluginAIDefinitionsEnabled = useEditorPluginAIDefinitionsEnabled();
	const editorPluginAIDefinitionsConfig = useEditorPluginAIDefinitionsConfig();

	const selectionExtensionPluginOptions = useSelectionExtensionPluginOptions({
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
		contentId: props.contentId,
		editorAPIRef,
		flags: props.flags,
		snippetExtensionList: props.snippetExtensionList,
	});

	const limitedModePluginOptions = {
		contentId: props.contentId,
		showFlag: props.flags.showFlag,
		killSwitchEnabled: StatsigConfigurations.isKillSwitchOn('cc_editor_perf_mode_kill_switch'),
	} as LimitedModePluginOptions;

	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showMarkdownSourceWritebackErrorFlag = useCallback(
		({ reason, source }: MarkdownSourceWritebackErrorInfo) => {
			if (source === 'live' && reason === 'parse-error') {
				return;
			}

			void props.flags.showErrorFlag({
				id: MARKDOWN_SOURCE_WRITEBACK_ERROR_FLAG_ID,
				title: intl.formatMessage(i18n.markdownSourceWritebackErrorTitle),
				description: intl.formatMessage(i18n.markdownSourceWritebackErrorDescription),
				isAutoDismiss: false,
			});
		},
		[intl, props.flags],
	);

	const emojiNodeDataProvider = useEmojiNodeDataProvider();
	// The provider is transport only. Platform evaluates and records the experiment
	// when an eligible mention renders, including mentions in the control cohort.
	const mentionNodeDataProvider = useMemo(() => createMentionNodeDataProvider(), []);

	// EDITOR-6558: resolve markdown-mode state at the preset layer so the
	// preset can configure the markdown plugin and downstream filters
	// (toolbar `+` insert, `/` quick-insert) accordingly. The hook reads
	// from the cache populated by `ContentHeaderUnifiedQuery` and is also
	// gated by the `cc-markdown-mode` experiment, so it returns `false`
	// for non-markdown pages and when the experiment is off.
	const isMarkdownMode = useIsMarkdownMode(isLivePage ? props.contentId : undefined);

	const contextSmartCardClient = useContext(SmartCardContext)?.connections?.client;
	const smartCardClient = props.smartCardContext?.connections?.client ?? contextSmartCardClient;
	const pasteOptionsToolbarPluginOptions = useMemo(() => {
		return expValEquals('confluence_editor_paste_3p_link_actions_menu', 'isEnabled', true)
			? createPasteOptionsToolbarPluginOptions({
					createAnalyticsEvent,
					parentId: props.contentId,
					smartCardClient,
					spaceKey: props.spaceKey,
				})
			: undefined;
	}, [createAnalyticsEvent, props.contentId, props.spaceKey, smartCardClient]);

	// EDITOR-6636 — sync `serializeFileMedia` for the markdown-mode source view.
	// Resolves `media (type: "file")` ids to filenames so we can emit
	// `/wiki/download/attachments/{contentId}/{filename}` proxy URLs that the
	// renderer can serve via the viewer's session cookie. No tokens / signed
	// URLs ever land in the markdown text. Returns `undefined` when contentId
	// isn't ready or markdown mode isn't enabled — in either case file media
	// gracefully degrades to alt-text-only.
	const serializeFileMediaForMarkdown = useFileMediaUrlSerializer(
		isMarkdownMode ? props.contentId : undefined,
	);

	const presetFunction = useCallback(() => {
		const {
			__livePage,
			__rendererExtensionOptions,
			allowFindReplace,
			annotationProviders,
			appearance,
			autoformattingProvider,
			editorPluginAIOptions,
			collabEdit,
			contextIdentifierProvider,
			elementBrowser,
			emojiProvider,
			extensionHandlers,
			getExtensionHeight,
			getHeadingLinkPath,
			shouldHideHeadingAnchorLinks,
			hasRequestedEditPermission,
			isAIEnabledForContent,
			isUnpublishedDraft,
			smartCardContext,
			linking,
			media,
			mention,
			mentionProvider,
			quickInsert,
			referentialityContext,
			requestToEditContent,
			taskDecisionProvider,
			viewMode,
			disabled,
			blockLinkHashPrefix,
			showDiffPluginParams,
			syncedBlockProvider,
			syncedBlockEventHandlers,
			onFeedbackPromptShown,
			onGiveFeedback,
			shouldShowFeedbackPrompt,
			disableFloatingToolbar,
			disableToolbar,
			contentId,
			spaceKey,
			contentMode,
		} = props;

		// IMPORTANT: Don't add spread operator (`...props`) here because it will make impossible to
		//            understand which props are used in the preset.
		const presetOptions: CreateFullPageEditorPresetProps = {
			__isRemixHidden: props.__isRemixHidden,
			// @ts-expect-error — property should be __isRovoLLMEnabled in CreateFullPageEditorPresetProps
			__isRovoLLMEnabled: props.__isRovoLLMEnabled,
			__remixDisabledReason: props.__remixDisabledReason,
			__livePage,
			__rendererExtensionOptions,
			allowFindReplace,
			annotationProviders,
			appearance,
			autoformattingProvider,
			collabEdit,
			contextIdentifierProvider,
			contextPanelOptions: {
				objectSideBar: {
					showPanel: showObjectSidebar,
					closePanel: hideObjectSidebar,
					closePanelById: hideObjectSidebarByPanelId,
				},
			},
			contentId,
			isMarkdownMode,
			pasteOptionsToolbarPluginOptions,
			spaceKey,
			contentMode,
			editorEngagementPlatformPluginConfig,
			editorPluginAIDefinitionsConfig,
			editorPluginAIDefinitionsEnabled,
			editorPluginAIOptions,
			elementBrowser,
			emojiProvider,
			emojiNodeDataProvider,
			mentionNodeDataProvider,
			extensionHandlers,
			flags: props.flags,
			getExtensionHeight,
			getHeadingLinkPath,
			shouldHideHeadingAnchorLinks,
			handleMentionsChanged: (changes: MentionType[]) => {
				if (changes.length) {
					setMentionChanges(changes);
				}
			},
			currentUserId: userId || undefined,
			getIsRovoPanelOpen,
			onAgentMentionChatClick,
			hasEditPermission: props.hasEditPermission,
			hasRequestedEditPermission,
			intl,
			isAIEnabledForContent,
			isUnpublishedDraft,
			isCompanyHub: props.isCompanyHub || false,
			isDesktop,
			isLoomPluginEnabled,
			limitedModePluginOptions,
			smartCardContext: isSSRStreaming() ? smartCardContext : undefined,
			linking,
			loomPluginOptions,
			media,
			mention,
			mentionProvider,
			prevAppearance: previousEditorProps?.appearance,
			quickInsert,
			referentialityContext,
			requestToEditContent,
			selectionExtensionPluginOptions,
			snippetsPluginOptions: props.snippetsPluginOptions,
			taskDecisionProvider,
			userPreferencesProvider: undefined,
			userPreferencesProviderNext: editorPreferencesProvider,
			viewMode,
			contextualFormattingEnabled: props.contextualFormattingEnabled,
			contextualFormattingMode: props.contextualFormattingMode,
			enableAIPlannerRemoteTextHighlight: props.enableAIPlannerRemoteTextHighlight,
			disabled,
			blockLinkHashPrefix,
			showDiffPluginParams,
			syncedBlockProvider,
			syncedBlockEventHandlers,
			onFeedbackPromptShown,
			onGiveFeedback,
			shouldShowFeedbackPrompt,
			disableFloatingToolbar,
			disableToolbar,
			trackChangesPluginOptions,
			isEmbeddedConfluence: props.isEmbeddedConfluence ?? false,
			duplicateMauiApp,
			cloudId,
			contentType: props.contentType,
			isRovoLLMEnabled,
			serializeFileMediaForMarkdown,
			onSourceWritebackError: showMarkdownSourceWritebackErrorFlag,
			aiSuggestionsPluginOptions: props.aiSuggestionsPluginOptions,
		};

		// In markdown mode, build a dedicated preset containing only the
		// curated set of plugins that round-trip cleanly through GFM. Both
		// presets accept the same `presetOptions` shape — the markdown
		// variant just consumes the subset of fields that are meaningful in
		// markdown mode.
		if (isMarkdownMode) {
			return createMarkdownEditorPreset(presetOptions);
		}

		return createFullPageEditorPreset({
			...presetOptions,
			enableAgentSectioning: expValEquals('platform_editor_agent_mentions', 'isEnabled', true),
		});
		// The dependency array should mirror the dependencies we pass to usePreset.
		// Refactor this to only use one usePreset when cleaning up confluence_frontend_transition_preloading_system FG
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		props.appearance,
		props.contentId,
		props.onFeedbackPromptShown,
		props.onGiveFeedback,
		props.shouldShowFeedbackPrompt,
		isMarkdownMode,
		isRovoLLMEnabled,
		serializeFileMediaForMarkdown,
	]);

	const { preset: presetStandard, editorApi: editorApiStandard } = usePreset(presetFunction, [
		// WARNING: Adding this dependency array causes the editor plugins to all recreate
		// This can be very expensive. Be very careful adding to this, avoid if possible!
		// Generally we can update props via their plugin APIs (see `placeholder` as an example)
		// update preset based on props change, originally from packages/editor/editor-core/src/create-editor/preset-utils.ts
		// persistScrollGutter not used, allowUndoRedoButtons and sanitizePrivateContent always true
		props.appearance,
		isMarkdownMode,
		isRovoLLMEnabled,
		props.onFeedbackPromptShown,
		props.onGiveFeedback,
		props.shouldShowFeedbackPrompt,
		serializeFileMediaForMarkdown,
	]);
	const { preset: presetSmootherTransitionsFG, editorApi: editorApiSmootherTransitionsFG } =
		usePreset(presetFunction, [
			props.appearance,
			props.contentId,
			isMarkdownMode,
			isRovoLLMEnabled,
			props.onFeedbackPromptShown,
			props.onGiveFeedback,
			props.shouldShowFeedbackPrompt,
			serializeFileMediaForMarkdown,
		]);

	const isPresetRerunningOnTransition =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		fg('confluence_frontend_transition_preloading_system') && isLivePage;
	const editorApi = isPresetRerunningOnTransition
		? editorApiSmootherTransitionsFG
		: editorApiStandard;
	const preset = isPresetRerunningOnTransition ? presetSmootherTransitionsFG : presetStandard;

	useEffect(() => {
		editorApi?.core?.actions.execute(
			editorApi?.placeholder?.commands.setPlaceholder(props.placeholder ?? ''),
		);
	}, [editorApi, props.placeholder]);

	useEffect(() => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('confluence_frontend_transition_preloading_system')) {
			editorApi?.media?.actions.setProvider(props.media?.provider);
		}
	}, [editorApi, props.media?.provider]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	// Register callback to capture limited mode status in INSM events
	useEffect(() => {
		if (editorApi && insm.session && !expVal('platform_editor_insm_removal', 'isEnabled', false)) {
			insm.session.addProperties(() => ({
				limitedModeEnabled: editorApi?.limitedMode?.sharedState.currentState()?.enabled ?? false,
			}));
		}
	}, [editorApi]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom as EditorAPI['loom'],
		providerConfig: loomProviderConfig,
	});

	return preset;
}
